import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';

const TurnoForm = ({ modo = 'transportista', soloUnProveedor = false, volverA = '/dashboard' }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fechaTurno, setFechaTurno] = useState(null);
  const [horasDisponibles, setHorasDisponibles] = useState([]);
  const [horasDisponiblesHasta, setHorasDisponiblesHasta] = useState([]);
  const [horaDesde, setHoraDesde] = useState('');
  const [horaHasta, setHoraHasta] = useState('');
  const [fechasDisponibles, setFechasDisponibles] = useState([]);
  const [horariosOcupados, setHorariosOcupados] = useState([]);
  const [observaciones, setObservaciones] = useState('');
  const [proveedores, setProveedores] = useState([
    {
      nombre: '',
      facturas: '',
      orden_compra: '',
      remito: '',
      documentos: [],
      bultos: { heladera: 0, caja: 0, bolsa: 0, pallet: 0, unidad: 0 }
    }
  ]);
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDisponibilidad = async () => {
      try {
        const responseFechas = await api.get('/turnos/disponibles');
        setFechasDisponibles(responseFechas.data.fechas.map(fecha => new Date(fecha)));

        if (fechaTurno) {
          const responseHorarios = await api.get(`/turnos/disponibles?fecha=${fechaTurno.toISOString().split('T')[0]}`);
          setHorariosOcupados(responseHorarios.data.horariosOcupados || []);
        }
      } catch (error) {
        console.error('❌ Error al obtener disponibilidad:', error);
        setError('Error al obtener disponibilidad de turnos.');
      }
    };

    fetchDisponibilidad();
  }, [fechaTurno]);

  const handleFechaChange = async (date) => {
    setFechaTurno(date);
    setHorasDisponibles([]);
    setHoraDesde('');
    setHoraHasta('');

    try {
      const response = await api.get(`/turnos/disponibles?fecha=${date.toISOString().split('T')[0]}`);
      setHorasDisponibles(response.data.horas);
    } catch (error) {
      setError('Error al obtener horarios para la fecha seleccionada.');
    }
  };

  const handleHoraDesdeChange = (horaSeleccionada) => {
    setHoraDesde(horaSeleccionada);
  
    if (soloUnProveedor) {
      const [h, m] = horaSeleccionada.split(':').map(Number);
      const date = new Date();
      date.setHours(h);
      date.setMinutes(m + 30);
      const nuevaHoraHasta = date.toTimeString().slice(0, 5);
      setHoraHasta(nuevaHoraHasta);
    } else {
      const opciones = horasDisponibles.filter(hora => {
        const diferencia = (new Date(`1970-01-01T${hora}:00`).getTime() - new Date(`1970-01-01T${horaSeleccionada}:00`).getTime()) / (1000 * 60);
        return (
          diferencia > 0 && diferencia <= 30 &&
          !horariosOcupados.some(turno => {
            const inicioTurno = new Date(`1970-01-01T${turno.desde}:00`).getTime();
            const finTurno = new Date(`1970-01-01T${turno.hasta}:00`).getTime();
            const horaEvaluada = new Date(`1970-01-01T${hora}:00`).getTime();
            return (
              (horaEvaluada > inicioTurno && horaEvaluada <= finTurno) || 
              (horaEvaluada + 1800000 > inicioTurno && horaEvaluada <= finTurno) || 
              (horaEvaluada - 1800000 < finTurno && horaEvaluada > inicioTurno)
            );
          })
        );
      });
  
      setHorasDisponiblesHasta(opciones);
    }
  };

  const handleProveedorChange = (index, field, value) => {
    const updatedProveedores = [...proveedores];
    updatedProveedores[index][field] = value;
    setProveedores(updatedProveedores);
  };

  const handleBultoChange = (index, tipo, value) => {
    const updatedProveedores = [...proveedores];
    updatedProveedores[index].bultos[tipo] = Math.max(0, parseInt(value) || 0);
    setProveedores(updatedProveedores);
  };

  const handleFileChange = (index, files) => {
    const updatedProveedores = [...proveedores];
    updatedProveedores[index].documentos = Array.from(files);
    setProveedores(updatedProveedores);
  };

  const agregarProveedor = () => {
    setProveedores([...proveedores, {
      nombre: '',
      facturas: '',
      orden_compra: '',
      remito: '',
      documentos: [],
      bultos: { heladera: 0, caja: 0, bolsa: 0, pallet: 0, unidad: 0 }
    }]);
  };

  const eliminarProveedor = (index) => {
    if (proveedores.length > 1) {
      setProveedores(proveedores.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMensaje('');
    setIsSubmitting(true);

    if (!fechaTurno || !horaDesde || !horaHasta) {
      setError('Debe seleccionar una fecha y un rango horario.');
      setIsSubmitting(false);
      return;
    }

    if (horaDesde >= horaHasta) {
      setError('La hora de inicio debe ser menor que la hora de fin.');
      setIsSubmitting(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('No estás autenticado.');
      setIsSubmitting(false);
      return;
    }

    const archivosInvalidos = proveedores.flatMap(prov =>
      (prov.documentos || []).filter(file => file.type !== 'application/pdf')
    );
    if (archivosInvalidos.length > 0) {
      setError('Uno o más archivos seleccionados no son PDF.');
      setIsSubmitting(false);
      return;
    }

    const proveedoresJSON = JSON.stringify(proveedores.map(prov => ({
      nombre: prov.nombre,
      facturas: prov.facturas ? prov.facturas.split(',').map(f => f.trim()) : [],
      orden_compra: prov.orden_compra || '',
      remito: prov.remito || '',
      bultos: prov.bultos || {}
    })));

    const formData = new FormData();
    formData.append('fecha_turno', fechaTurno.toISOString().split('T')[0]);
    formData.append('hora_desde', horaDesde);
    formData.append('hora_hasta', horaHasta);
    formData.append('observaciones', observaciones || '');
    formData.append('proveedor_bultos', proveedoresJSON);

    proveedores.forEach((prov) => {
      if (prov.documentos && prov.documentos.length > 0) {
        prov.documentos.forEach((file) => {
          formData.append('documentos', file);
        });
      }
    });

    try {
      await api.post('/turnos', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setMensaje('Turno solicitado con éxito. Pendiente de aprobación.');
      setTimeout(() => navigate(volverA), /* 1500 */);
    } catch (error) {
      console.error('❌ Error al solicitar turno:', error);
      setError(error.response?.data?.error || 'Error al solicitar el turno.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mt-5">
      <button
        className="btn btn-secondary"
        onClick={() => navigate(volverA)}
      >
        ⬅ Volver
      </button>

      <br /><br />
      <h2>Solicitar Turno</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      {mensaje && <div className="alert alert-success">{mensaje}</div>}

      <form onSubmit={handleSubmit}>
        <label>Fecha del Turno</label><br />
        <DatePicker
          selected={fechaTurno}
          onChange={handleFechaChange}
          includeDates={fechasDisponibles}
          dateFormat="yyyy-MM-dd"
          className="form-control"
          required
        />

        <br />
        <label>Hora Desde</label>
        <select className="form-control" value={horaDesde} onChange={(e) => handleHoraDesdeChange(e.target.value)} required>
          <option value="">Selecciona una hora</option>
          {horasDisponibles.map((hora, index) => (
            <option key={index} value={hora}>{hora}</option>
          ))}
        </select>

        {!soloUnProveedor ? (
          <>
            <label>Hora Hasta</label>
            <select className="form-control" value={horaHasta} onChange={(e) => setHoraHasta(e.target.value)} required>
              <option value="">Selecciona una hora</option>
              {horasDisponiblesHasta.map((hora, index) => (
                <option key={index} value={hora}>{hora}</option>
              ))}
            </select>
          </>
        ) : (
          horaHasta && (
            <p className="mt-2 text-muted">
              El turno finalizará automáticamente a las <strong>{horaHasta}</strong> hs
            </p>
          )
        )}

        <label>Observaciones (opcional)</label>
        <textarea className="form-control" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} rows="3" maxLength="140" placeholder="Ingrese observaciones..." />
        <hr />

        {proveedores.map((proveedor, index) => (
          <div key={index} className="border p-3 mb-3">
            <h5>Proveedor {index + 1}</h5>
            <input type="text" className="form-control" placeholder="Nombre del Proveedor" value={proveedor.nombre} onChange={(e) => handleProveedorChange(index, 'nombre', e.target.value)} required />
            <br />
            <input type="text" className="form-control" placeholder="Facturas (separadas por coma)" value={proveedor.facturas} onChange={(e) => handleProveedorChange(index, 'facturas', e.target.value)} required />
            <br />
            <input type="text" className="form-control" placeholder="Orden de Compra (opcional)" value={proveedor.orden_compra} onChange={(e) => handleProveedorChange(index, 'orden_compra', e.target.value)} />
            <br />
            <input type="text" className="form-control" placeholder="Número de Remito" value={proveedor.remito} onChange={(e) => handleProveedorChange(index, 'remito', e.target.value)} />
            <br />
            <input type="file" className="form-control" accept="application/pdf" multiple onChange={(e) => handleFileChange(index, e.target.files)} />
            <br />
            <label>Tipos de Bultos</label>
            <div className="row">
              {Object.entries(proveedor.bultos).map(([tipo, cantidad]) => (
                <div key={`${index}-${tipo}`} className="col-md-2 d-flex flex-column align-items-center">
                  <label>{tipo.charAt(0).toUpperCase() + tipo.slice(1)}</label>
                  <input type="number" className="form-control text-center" min="0" value={cantidad} onChange={(e) => handleBultoChange(index, tipo, e.target.value)} />
                </div>
              ))}
            </div>

            {!soloUnProveedor && (
              <button type="button" className="btn btn-danger mt-2" onClick={() => eliminarProveedor(index)}>Eliminar Proveedor</button>
            )}
          </div>
        ))}

        {!soloUnProveedor && (
          <button type="button" className="btn btn-secondary mt-3" onClick={agregarProveedor}>Agregar otro proveedor</button>
        )}
        <br />
        <button type="submit" className="btn btn-primary mt-3" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Cargando...
            </>
          ) : (
            'Solicitar Turno'
          )}
        </button>
      </form>
    </div>
  );
};

export default TurnoForm;

